import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import { ArrowLeft, Banknote, Clock, FileCheck, FileText, Loader2, Receipt, Upload, Wallet } from 'lucide-react';
import { toast } from 'sonner';
import { DriverBottomNav } from '../components/DriverBottomNav';
import { accountingService } from '../services/accountingService';
import { documentService } from '../services/documentService';
import { DriverMobilePersonnelCenter } from '../components/DriverMobilePersonnelCenter';
import { useAutoRefresh } from '../hooks/useAutoRefresh';
import { useAuth } from '../contexts/AuthContext';
import { hasUserRole } from '../utils/userRoles';

const resolveFileUrl = (filePath?: string | null) => {
  if (!filePath) return '#';
  if (/^https?:\/\//i.test(filePath)) return filePath;
  const apiBase = (import.meta.env.VITE_API_URL || 'http://localhost:5000/api').replace(/\/api\/?$/, '');
  return `${apiBase}${filePath.startsWith('/') ? filePath : `/${filePath}`}`;
};

type AccountingShipment = {
  id: string;
  shipmentNo: string;
  pickupCity: string;
  deliveryCity: string;
  accountingStatus: string;
  price: number;
  ugramaFee?: number;
  waitingFee: number;
  hammaliyeFee?: number;
  beklemeFee?: number;
  yatmaFee?: number;
  invoiceAmount: number;
  finalInvoiceAmount: number;
  invoiceCode?: string | null;
  stokbarVehiclePlate?: string | null;
  paymentDueDate?: string | null;
  paymentCompletedAt?: string | null;
  deliveryApprovedAt?: string | null;
  invoiceApprovedAt?: string | null;
  deliveryDocument?: { fileName: string; filePath: string } | null;
  invoiceDocument?: { fileName: string; filePath: string } | null;
  receiptDocument?: { fileName: string; filePath: string } | null;
};

export function DriverMobileEarnings() {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const isCorporateCarrierUser = hasUserRole(user, 'corporate-external-transport', 'corporate-external-transport-user');
  const [isInternalDriver, setIsInternalDriver] = useState(false);
  const [loading, setLoading] = useState(true);
  const [shipments, setShipments] = useState<AccountingShipment[]>([]);
  const [invoiceShipment, setInvoiceShipment] = useState<AccountingShipment | null>(null);
  const [invoiceFile, setInvoiceFile] = useState<File | null>(null);
  const [invoiceForm, setInvoiceForm] = useState({ invoiceAmount: '', waitingFee: '', hammaliyeFee: '', beklemeFee: '', yatmaFee: '', finalInvoiceAmount: '', invoiceCode: '', stokbarVehiclePlate: '' });
  const [saving, setSaving] = useState(false);
  const invoiceFileInputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    const userData = sessionStorage.getItem('driverMobileUser');
    if (!userData) {
      if (authLoading) return;
      if (hasUserRole(user, 'corporate-external-transport', 'corporate-external-transport-user')) {
        loadData();
        return;
      }
      navigate('/driver-mobile-auth', { replace: true });
      return;
    }
    const parsedUser = JSON.parse(userData);
    if (parsedUser?.role === 'internal-driver' || parsedUser?.driverType === 'internal') {
      setIsInternalDriver(true);
      setLoading(false);
      return;
    }
    loadData();
  }, [authLoading, navigate, user]);

  useAutoRefresh(
    () => {
      if (isInternalDriver) return;
      return loadData();
    },
    { intervalMs: 15000, enabled: !isInternalDriver }
  );

  const loadData = async () => {
    try {
      setLoading(true);
      const response = await accountingService.getDriverShipments();
      setShipments((response.data || response || []) as AccountingShipment[]);
    } catch (error) {
      console.error('Driver accounting load failed:', error);
    } finally {
      setLoading(false);
    }
  };

  const grouped = useMemo(() => ({
    invoiceWaiting: shipments.filter((item) => item.accountingStatus === 'invoice-waiting' || item.accountingStatus === 'invoice-rejected'),
    paymentWaiting: shipments.filter((item) => item.accountingStatus === 'invoice-approval-pending' || item.accountingStatus === 'payment-pending'),
    completed: shipments.filter((item) => item.accountingStatus === 'payment-completed'),
  }), [shipments]);

  const stats = {
    pendingInvoiceCount: grouped.invoiceWaiting.length,
    paymentWaitingCount: grouped.paymentWaiting.length,
    completedTotal: grouped.completed.reduce((sum, item) => sum + Number(item.finalInvoiceAmount || item.price || 0), 0),
    paidCount: grouped.completed.length,
  };

  const openInvoiceModal = (shipment: AccountingShipment) => {
    setInvoiceShipment(shipment);
    setInvoiceFile(null);
    setInvoiceForm({
      invoiceAmount: String(shipment.price || 0),
      waitingFee: String(shipment.waitingFee || 0),
      hammaliyeFee: String(shipment.hammaliyeFee || 0),
      beklemeFee: String(shipment.beklemeFee || shipment.waitingFee || 0),
      yatmaFee: String(shipment.yatmaFee || 0),
      finalInvoiceAmount: String(shipment.finalInvoiceAmount || Number(shipment.price || 0) + Number(shipment.hammaliyeFee || 0) + Number(shipment.beklemeFee || shipment.waitingFee || 0)),
      invoiceCode: shipment.invoiceCode || '',
      stokbarVehiclePlate: shipment.stokbarVehiclePlate || '',
    });
    if (invoiceFileInputRef.current) {
      invoiceFileInputRef.current.value = '';
    }
  };

  const onInvoiceValueChange = (field: string, value: string) => {
    const next = { ...invoiceForm, [field]: value };
    const invoiceAmount = Number(field === 'invoiceAmount' ? value : next.invoiceAmount || 0);
    const hammaliyeFee = Number(field === 'hammaliyeFee' ? value : next.hammaliyeFee || 0);
    const beklemeFee = Number(field === 'beklemeFee' ? value : next.beklemeFee || 0);
    next.finalInvoiceAmount = String(invoiceAmount + hammaliyeFee + beklemeFee);
    setInvoiceForm(next);
  };

  const handleInvoiceFileChange = async (file: File | null) => {
    setInvoiceFile(file);
    if (file) {
      toast.success(`Dosya secildi: ${file.name}`);
    }
  };

  const submitInvoice = async () => {
    if (!invoiceShipment || !invoiceFile) {
      toast.error('Fatura dosyasi zorunludur');
      return;
    }

    try {
      setSaving(true);
      toast.info('Fatura yukleniyor...');
      const formData = new FormData();
      formData.append('file', invoiceFile);
      formData.append('entityType', 'shipment');
      formData.append('entityId', String(invoiceShipment.id));
      formData.append('documentType', 'invoice');
      formData.append('name', invoiceFile.name);
      formData.append('ocrMetadata', JSON.stringify({ invoiceCode: invoiceForm.invoiceCode || null }));
      const uploadResult = await documentService.uploadDocument(formData);

      await accountingService.submitInvoice(invoiceShipment.id, {
        invoiceAmount: Number(invoiceForm.invoiceAmount || 0),
        waitingFee: Number(invoiceForm.waitingFee || 0),
        hammaliyeFee: Number(invoiceForm.hammaliyeFee || 0),
        beklemeFee: Number(invoiceForm.beklemeFee || invoiceForm.waitingFee || 0),
        yatmaFee: Number(invoiceForm.yatmaFee || 0),
        finalInvoiceAmount: Number(invoiceForm.finalInvoiceAmount || 0),
        invoiceCode: invoiceForm.invoiceCode,
        invoiceDocumentId: uploadResult.data?.id,
        stokbarLoadingCode: invoiceForm.invoiceCode,
        stokbarVehiclePlate: invoiceForm.stokbarVehiclePlate,
      });

      setInvoiceShipment(null);
      setInvoiceFile(null);
      await loadData();
      toast.success('Fatura basariyla yuklendi ve onaya gonderildi');
    } catch (error: any) {
      console.error('Invoice submit failed:', error);
      toast.error(error?.message || 'Fatura yuklenemedi');
    } finally {
      setSaving(false);
    }
  };

  const renderCard = (shipment: AccountingShipment, actions?: React.ReactNode, statusText?: string) => (
    <div key={shipment.id} className="bg-white rounded-2xl shadow-sm border border-gray-200 p-4 space-y-3">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="text-sm font-bold text-slate-900">{shipment.shipmentNo}</div>
          <div className="text-xs text-slate-500 mt-1">{shipment.pickupCity} → {shipment.deliveryCity}</div>
        </div>
        {statusText && <span className="px-2 py-1 rounded-full bg-blue-50 text-blue-700 text-xs font-medium">{statusText}</span>}
      </div>
      <div className="grid grid-cols-2 gap-3 text-sm">
        <div>
          <p className="text-gray-500 text-xs">Teklif Bedeli</p>
          <p className="font-semibold text-gray-900">{Number(shipment.price || 0).toLocaleString('tr-TR')} TL</p>
        </div>
        <div>
          <p className="text-gray-500 text-xs">Nihai Tutar</p>
          <p className="font-semibold text-emerald-700">{Number(shipment.finalInvoiceAmount || shipment.price || 0).toLocaleString('tr-TR')} TL</p>
        </div>
        {shipment.paymentDueDate && (
          <div>
            <p className="text-gray-500 text-xs">En Gec Odeme</p>
            <p className="font-medium text-gray-900">{new Date(shipment.paymentDueDate).toLocaleDateString('tr-TR')}</p>
          </div>
        )}
        {shipment.invoiceApprovedAt && (
          <div>
            <p className="text-gray-500 text-xs">Muhasebe Onay Tarihi</p>
            <p className="font-medium text-gray-900">{new Date(shipment.invoiceApprovedAt).toLocaleDateString('tr-TR')}</p>
          </div>
        )}
        {shipment.invoiceCode && (
          <div>
            <p className="text-gray-500 text-xs">Yukleme Kodu</p>
            <p className="font-medium text-gray-900">{shipment.invoiceCode}</p>
          </div>
        )}
      </div>
      {actions}
    </div>
  );

  if (isInternalDriver) {
    return <DriverMobilePersonnelCenter />;
  }

  if (loading) {
    return <div className="safe-area-page min-h-screen bg-gray-50 flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-blue-600" /></div>;
  }

  return (
    <div className="safe-area-page min-h-dvh bg-gray-50 dark:bg-[#0b0b0b]">
      <div className="border-b border-slate-200 bg-white p-4 pb-6 shadow-sm dark:border-slate-800 dark:bg-gradient-to-r dark:from-emerald-700 dark:to-teal-700 dark:text-white dark:shadow-lg">
        <div className="flex items-center justify-between mb-5">
          <button onClick={() => navigate(-1)} className="inline-flex items-center gap-2 rounded-xl bg-slate-100 px-3 py-2 text-slate-900 ring-1 ring-slate-200 transition-colors hover:bg-slate-200 dark:bg-white/15 dark:text-white dark:ring-white/20 dark:hover:bg-white/20"><ArrowLeft className="w-5 h-5" /><span>Geri</span></button>
          <h1 className="text-lg font-bold text-slate-900 dark:text-white">Muhasebe</h1>
          <div className="w-9" />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div className="rounded-2xl border border-slate-200 bg-slate-50 p-3 dark:border-white/10 dark:bg-white/10"><p className="text-xs text-slate-600 dark:text-emerald-100">Fatura Bekleyen</p><p className="mt-1 text-2xl font-bold text-slate-900 dark:text-white">{stats.pendingInvoiceCount}</p></div>
          <div className="rounded-2xl border border-slate-200 bg-slate-50 p-3 dark:border-white/10 dark:bg-white/10"><p className="text-xs text-slate-600 dark:text-emerald-100">Odeme Bekleyen</p><p className="mt-1 text-2xl font-bold text-slate-900 dark:text-white">{stats.paymentWaitingCount}</p></div>
          <div className="rounded-2xl border border-slate-200 bg-slate-50 p-3 dark:border-white/10 dark:bg-white/10"><p className="text-xs text-slate-600 dark:text-emerald-100">Odenen Sevkiyat</p><p className="mt-1 text-2xl font-bold text-slate-900 dark:text-white">{stats.paidCount}</p></div>
          <div className="rounded-2xl border border-slate-200 bg-slate-50 p-3 dark:border-white/10 dark:bg-white/10"><p className="text-xs text-slate-600 dark:text-emerald-100">Toplam Tahsilat</p><p className="mt-1 text-2xl font-bold text-slate-900 dark:text-white">{stats.completedTotal.toLocaleString('tr-TR')} ₺</p></div>
        </div>
      </div>

      <div className="p-4 space-y-6">
        <section>
          <div className="flex items-center gap-2 mb-3"><Receipt className="w-5 h-5 text-emerald-700" /><h2 className="text-base font-bold text-slate-900">Fatura Bekleyen Sevkiyatlar</h2></div>
          <div className="space-y-3">
            {grouped.invoiceWaiting.map((shipment) => renderCard(shipment, <button onClick={() => openInvoiceModal(shipment)} className="w-full inline-flex items-center justify-center gap-2 rounded-xl bg-emerald-600 text-white py-2.5 font-medium"><Upload className="w-4 h-4" />Fatura Yukle</button>, shipment.accountingStatus === 'invoice-rejected' ? 'Fatura Reddedildi' : 'Fatura Bekliyor'))}
            {grouped.invoiceWaiting.length === 0 && <div className="rounded-2xl border border-slate-200 bg-white p-5 text-sm text-gray-500 dark:border-slate-800 dark:bg-[#111111] dark:text-slate-400">Fatura bekleyen sevkiyat bulunmuyor.</div>}
          </div>
        </section>

        <section>
          <div className="flex items-center gap-2 mb-3"><Clock className="w-5 h-5 text-orange-600" /><h2 className="text-base font-bold text-slate-900">Odeme Sureci</h2></div>
          <div className="space-y-3">
            {grouped.paymentWaiting.map((shipment) => renderCard(shipment, <p className="text-xs text-blue-700 bg-blue-50 rounded-xl px-3 py-2">{shipment.accountingStatus === 'invoice-approval-pending' ? 'Fatura muhasebe onayi bekliyor.' : 'Muhasebe onayi sonrasi odemeniz 10 ile 15 is gunu icerisinde gerceklestirilecektir.'}</p>, shipment.accountingStatus === 'invoice-approval-pending' ? 'Fatura Onayi' : 'Odeme Bekliyor'))}
            {grouped.paymentWaiting.length === 0 && <div className="rounded-2xl border border-slate-200 bg-white p-5 text-sm text-gray-500 dark:border-slate-800 dark:bg-[#111111] dark:text-slate-400">Aktif odeme sureci bulunmuyor.</div>}
          </div>
        </section>

        <section>
          <div className="flex items-center gap-2 mb-3"><Banknote className="w-5 h-5 text-emerald-700" /><h2 className="text-base font-bold text-slate-900">Odeme Tamamlananlar</h2></div>
          <div className="space-y-3">
            {grouped.completed.map((shipment) => renderCard(shipment, shipment.receiptDocument ? <a href={resolveFileUrl(shipment.receiptDocument.filePath)} target="_blank" rel="noreferrer" className="w-full inline-flex items-center justify-center gap-2 rounded-xl border border-emerald-200 bg-emerald-50 text-emerald-700 py-2.5 font-medium"><FileText className="w-4 h-4" />Dekontu Goruntule</a> : undefined, 'Odendi'))}
            {grouped.completed.length === 0 && <div className="rounded-2xl border border-slate-200 bg-white p-5 text-sm text-gray-500 dark:border-slate-800 dark:bg-[#111111] dark:text-slate-400">Henüz tamamlanan odeme bulunmuyor.</div>}
          </div>
        </section>
      </div>

      {invoiceShipment && (
        <div className="fixed inset-0 bg-black/50 flex items-end sm:items-center justify-center z-50 p-3 sm:p-4">
          <div className="w-full max-w-lg max-h-[calc(100vh-5rem)] sm:max-h-[90vh] overflow-y-auto rounded-t-3xl sm:rounded-3xl bg-white dark:bg-[#111111] p-4 sm:p-5 space-y-4 pb-28 sm:pb-5">
            <div className="sticky top-0 z-10 flex items-center justify-between border-b border-slate-200 bg-white py-2 dark:border-slate-800 dark:bg-[#111111]">
              <h3 className="text-lg font-bold text-slate-900">Fatura Yukle</h3>
              <button onClick={() => setInvoiceShipment(null)} className="text-sm text-gray-500 dark:text-slate-400">Kapat</button>
            </div>
            <div className="sticky top-10 z-[5] rounded-2xl border border-amber-300 bg-amber-50 px-4 py-3 text-sm font-semibold text-amber-900 shadow-sm">
              Faturada işleme alma süreçlerinde sorun yaşamamak adına açıklama kısmına <span className="font-extrabold">Sevk Kodu: {invoiceShipment.shipmentNo}</span> şeklinde mutlaka giriniz.
            </div>
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div><p className="text-gray-500 text-xs">Teklif Bedeli</p><p className="font-semibold">{Number(invoiceShipment.price || 0).toLocaleString('tr-TR')} TL</p></div>
              <div><p className="text-gray-500 text-xs">Plaka</p><p className="font-semibold">{invoiceShipment.vehiclePlate || '-'}</p></div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <input value={invoiceForm.invoiceAmount} onChange={(e) => onInvoiceValueChange('invoiceAmount', e.target.value)} type="number" step="0.01" placeholder="Teklif bedeli" className="rounded-xl border border-slate-300 bg-white px-3 py-2 text-slate-900 dark:border-slate-700 dark:bg-[#171717] dark:text-slate-100" />
              <input value={invoiceForm.hammaliyeFee} onChange={(e) => onInvoiceValueChange('hammaliyeFee', e.target.value)} type="number" step="0.01" placeholder="Hammaliye bedeli" className="rounded-xl border border-slate-300 bg-white px-3 py-2 text-slate-900 dark:border-slate-700 dark:bg-[#171717] dark:text-slate-100" />
              <input value={invoiceForm.beklemeFee} onChange={(e) => onInvoiceValueChange('beklemeFee', e.target.value)} type="number" step="0.01" placeholder="Bekleme bedeli" className="rounded-xl border border-slate-300 bg-white px-3 py-2 text-slate-900 dark:border-slate-700 dark:bg-[#171717] dark:text-slate-100" />
              <input value={invoiceForm.yatmaFee} onChange={(e) => onInvoiceValueChange('yatmaFee', e.target.value)} type="number" step="0.01" placeholder="Yatma bedeli" className="rounded-xl border border-slate-300 bg-white px-3 py-2 text-slate-900 dark:border-slate-700 dark:bg-[#171717] dark:text-slate-100" />
              <input value={invoiceForm.finalInvoiceAmount} readOnly placeholder="Nihai tutar" className="rounded-xl border border-slate-300 bg-slate-50 px-3 py-2 text-slate-900 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100" />
              <input value={invoiceForm.stokbarVehiclePlate} onChange={(e) => setInvoiceForm((current) => ({ ...current, stokbarVehiclePlate: e.target.value }))} placeholder="Plaka eslestirme" className="rounded-xl border border-slate-300 bg-white px-3 py-2 text-slate-900 dark:border-slate-700 dark:bg-[#171717] dark:text-slate-100" />
            </div>
            <input value={invoiceForm.invoiceCode} onChange={(e) => setInvoiceForm((current) => ({ ...current, invoiceCode: e.target.value }))} placeholder="Yukleme kodu" className="w-full rounded-xl border border-slate-300 bg-white px-3 py-2 text-slate-900 dark:border-slate-700 dark:bg-[#171717] dark:text-slate-100" />
            <div className="rounded-2xl border-2 border-dashed border-slate-300 p-4 text-center text-sm text-slate-600">
              <input
                ref={invoiceFileInputRef}
                type="file"
                accept="image/*,.pdf,application/pdf"
                className="hidden"
                onChange={(e) => handleInvoiceFileChange(e.target.files?.[0] || null)}
              />
              <button
                type="button"
                onClick={() => invoiceFileInputRef.current?.click()}
                className="inline-flex items-center justify-center gap-2 rounded-xl bg-slate-900 px-4 py-2 text-sm font-medium text-white"
              >
                <Upload className="h-4 w-4" />
                Fatura PDF / gorsel sec
              </button>
              {invoiceFile ? (
                <div className="mt-3 rounded-xl bg-emerald-50 px-3 py-2 text-left text-emerald-700">
                  <div className="font-medium">Dosya secildi</div>
                  <div className="mt-1 break-all text-xs">{invoiceFile.name}</div>
                </div>
              ) : (
                <div className="mt-3 text-xs text-slate-500">Henuz dosya secilmedi</div>
              )}
            </div>
            <div className="bg-slate-50 rounded-2xl p-3 text-xs text-slate-600">
              Yukleme kodu faturadan OCR ile okunmayacak. Gerekirse bu alani manuel doldurabilir veya bos birakabilirsiniz.
            </div>
            <button onClick={submitInvoice} disabled={saving || !invoiceFile} className="w-full inline-flex items-center justify-center gap-2 rounded-xl bg-emerald-600 text-white py-3 font-medium disabled:bg-emerald-300 sticky bottom-0">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Wallet className="w-4 h-4" />}Faturayi Onaya Gonder
            </button>
          </div>
        </div>
      )}

      {!isCorporateCarrierUser ? <DriverBottomNav /> : null}
    </div>
  );
}
